import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { CircleCheckmarkIcon } from '../icons';
import { tokens } from '../theme/tokens';

const MARKER_SIZE = 20;
const DOT_SIZE = 10;
const RING_SIZE = (20.3 / 24) * MARKER_SIZE;
const RING_STROKE = (2 / 24) * MARKER_SIZE;

export type TimelineItemState = 'complete' | 'current' | 'upcoming';

export interface TimelineItem {
	id: string;
	title: string;
	description?: string;
	state?: TimelineItemState;
}

export type StepTimelineVariant = 'clickable' | 'non-clickable';

export type StepTimelineProps = {
	items: Array<TimelineItem>;
	activeIndex: number;
	onItemClick?: (itemIndex: number) => void;
	variant?: StepTimelineVariant;
	showContinuousTimeLine?: boolean;
};

type Appearance = TimelineItemState | 'selected' | 'idle';

function deriveState(index: number, activeIndex: number): TimelineItemState {
	if (index < activeIndex) {
		return 'complete';
	}

	if (index === activeIndex) {
		return 'current';
	}

	return 'upcoming';
}

const Container = styled.ol<{ $continuous?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.group}px;
	margin: 0;
	padding: ${tokens.padding.compact}px 0 ${tokens.padding.compact}px ${tokens.padding.container}px;
	list-style: none;
	box-sizing: border-box;

	${(props) =>
		props.$continuous &&
		css`
			min-height: 100%;
		`}
`;

const Track = styled.span<{ $continuous?: boolean }>`
	position: absolute;
	left: calc(${tokens.padding.container}px + ${MARKER_SIZE / 2}px);
	top: calc(${tokens.padding.compact}px + 11px);
	bottom: ${(props) => (props.$continuous ? '0' : `calc(${tokens.padding.compact}px + 11px)`)};
	width: 1px;
	background: ${tokens.border.subtle};
	transform: translateX(-50%);
	pointer-events: none;
`;

const rowReset = css<{ $stacked?: boolean }>`
	position: relative;
	display: flex;
	align-items: ${(props) => (props.$stacked ? 'flex-start' : 'center')};
	gap: ${tokens.gap.list}px;
	width: 100%;
	margin: 0;
	padding: 0;
	border: 0;
	background: transparent;
	text-align: left;
	font: inherit;
	color: inherit;
	min-height: 22px;
`;

const CopyStack = styled.span`
	display: flex;
	flex-direction: column;
	min-width: 0;
	gap: ${tokens.gap.inline}px;
`;

const Meta = styled.span`
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	line-height: ${tokens.typography.lineHeights.small};
	letter-spacing: 0.01em;
	color: ${tokens.text.tertiary};
`;

const Copy = styled.span<{ $emphasis?: boolean }>`
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	letter-spacing: 0.01em;
	color: ${(props) => (props.$emphasis ? tokens.text.primary : tokens.text.secondary)};
	font-weight: ${(props) =>
		props.$emphasis ? tokens.typography.weights.medium : tokens.typography.weights.regular};
`;

const Item = styled.li`
	display: block;
	margin: 0;
	padding: 0;
`;

const Row = styled.div`
	${rowReset}
	cursor: default;
`;

const ButtonRow = styled.button`
	${rowReset}
	cursor: pointer;
	border-radius: ${tokens.radius.xs}px;

	&:hover ${Copy} {
		color: ${tokens.text.primary};
	}

	&:focus-visible {
		outline: 2px solid ${tokens.border.focus};
		outline-offset: ${tokens.gap.inline}px;
	}
`;

const Marker = styled.span`
	width: ${MARKER_SIZE}px;
	height: ${MARKER_SIZE}px;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	z-index: 1;
	color: inherit;
	pointer-events: none;

	svg {
		display: block;
		width: ${MARKER_SIZE}px;
		height: ${MARKER_SIZE}px;
	}
`;

const Dot = styled.span<{ $color: string }>`
	width: ${DOT_SIZE}px;
	height: ${DOT_SIZE}px;
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => props.$color};
	flex-shrink: 0;
`;

const Ring = styled.span`
	width: ${RING_SIZE}px;
	height: ${RING_SIZE}px;
	box-sizing: border-box;
	border-radius: ${tokens.radius.full}px;
	border: ${RING_STROKE}px solid ${tokens.border.subtle};
	background: ${tokens.surface.white};
	flex-shrink: 0;
`;

function MarkerGlyph(props: { appearance: Appearance }): ReactNode {
	if (props.appearance === 'complete') {
		return <CircleCheckmarkIcon size={MARKER_SIZE} color={tokens.icon.tertiary} />;
	}

	if (props.appearance === 'current') {
		return <CircleCheckmarkIcon size={MARKER_SIZE} color={tokens.success.icon} />;
	}

	if (props.appearance === 'selected') {
		return <Dot $color={tokens.brand.icon} />;
	}

	if (props.appearance === 'idle') {
		return <Dot $color={tokens.border.subtle} />;
	}

	return <Ring />;
}

export function StepTimeline({
	items,
	activeIndex,
	onItemClick,
	variant = 'clickable',
	showContinuousTimeLine = true,
}: StepTimelineProps) {
	const clickable = variant === 'clickable';

	return (
		<Container $continuous={showContinuousTimeLine}>
			{items.length > 1 && <Track $continuous={showContinuousTimeLine} />}
			{items.map((item, index) => {
				const state = item.state ?? deriveState(index, activeIndex);
				const appearance: Appearance = clickable
					? index === activeIndex
						? 'selected'
						: 'idle'
					: state;
				const emphasis = appearance === 'current' || appearance === 'selected';
				const stacked = Boolean(item.description);

				const body = (
					<>
						<Marker>
							<MarkerGlyph appearance={appearance} />
						</Marker>
						<CopyStack>
							<Copy $emphasis={emphasis}>{item.title}</Copy>
							{item.description ? <Meta>{item.description}</Meta> : null}
						</CopyStack>
					</>
				);

				if (clickable) {
					return (
						<Item key={item.id}>
							<ButtonRow
								type="button"
								$stacked={stacked}
								aria-current={index === activeIndex ? 'step' : undefined}
								onClick={() => onItemClick?.(index)}
							>
								{body}
							</ButtonRow>
						</Item>
					);
				}

				return (
					<Item key={item.id}>
						<Row $stacked={stacked}>{body}</Row>
					</Item>
				);
			})}
		</Container>
	);
}
